/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 */

// set up polyfills first - note clientPolyfills.ts is listed as having sideEffects in package.json
import './clientPolyfills'

import { ChakraProvider, extendTheme } from '@chakra-ui/react'
import { loadableReady } from '@loadable/component'
import { OptimizelyProvider, enums as optimizelyEnums } from '@optimizely/react-sdk'
import { ReferrerProvider } from '@zcode-ui/account'
import { ChakraZ4 } from '@zcode-ui/chakra-themes'
import { composeBlueshiftEvent as composeForYouPayload } from '@zcode-ui/for-you'
import {
  checkLoggedIn,
  getUser,
  selectIsBot,
  selectIsMobile,
} from '@zcode-ui/web-zumper-global-state'
import { event, toTraktorEventName } from '@zcode-ui/web-zumper-integrations'
import { User } from '@zcode/bizos'
import { setOptimizelyClient } from '@zcode/integrations'
import { isNativeAuth } from '@zcode/legacy-local-storage'
import { ConsoleLogger, setGlobalLogger } from '@zcode/logger'
import {
  EVENT_NAMES,
  PROP_NAMES,
  alias,
  getOptimizelyVariationList,
  identify,
  register,
  track,
} from '@zcode/mixpanel'
import {
  AnalyticsC,
  isFailedToFetchError,
  isRateLimitError,
  localStorageSingleton,
  saveUserToLocalStorage,
  trackGAEvent,
} from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import {
  GeoLocationProvider,
  initializeGeoLocation,
} from '@zcode/nr/es/shared/components/GeoLocation'
import { IS_NOT_PRODUCTION } from '@zcode/nr/es/shared/env'
import { trackSignup } from '@zcode/nr/es/shared/modules/traktor/thunks'
import { replace } from '@zcode/nr/es/shared/packages/history-middleware/actions'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import {
  StaticHTMLProvider,
  createBrowserStaticRender,
  getStaticMap,
} from '@zcode/nr/es/shared/packages/static-hydrating-section'
import { ACCOUNT_CREATED } from '@zcode/nr/es/shared/thirdParty/optimizely/constants'
import getBrowserHistory from '@zcode/nr/es/shared/utils/browserHistory'
import Marketing from '@zcode/nr/es/shared/utils/marketing'
import * as NameUtils from '@zcode/nr/es/shared/utils/name'
import { performanceBegin, performanceEnd } from '@zcode/nr/es/shared/utils/performance'
import { getRecommIdQueryParam } from '@zcode/nr/es/shared/utils/recommendations/helpers'
import { getVisitedExperiments } from '@zcode/optimizely-client'
import { isCypress } from '@zcode/utils'
import { routesConstants } from '@zcode/zumper-routes'
import cookies from 'browser-cookies'
import React, { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'
import { CompatRouter } from 'react-router-dom-v5-compat'
import { loginHandler } from './actions/bundle.action'
import { addSuccessButterBar } from './actions/butter.action'
import { clearBundle, setBundle, setLocalUser } from './actions/index'
import { processPendingMessage } from './actions/sendMessage/pendingMessage'
import { AppContainer } from './components/App/AppContainer'
import { logger } from './logger'
import { setFromPaidMarketing } from './modules/currentSearch/actions/index'
import { getPageNameFromRouteProps } from './modules/mixpanel/helpers'
import { trackWebVitalsMetricsEvent } from './modules/traktor/thunks/trackWebVitalsMetricsEvent'
import { addForYouTokens } from './utils/addForYouTokens'
import { getPageTypeIPromiseImOnlyCallingThisClientSide } from './utils/getPageType'
import { initSentry } from './utils/initSentry'
import { initializeOptimizely } from './utils/initializeOptimizely'
import { default as createNzrStore } from './utils/store'
import { syncLocalStorageToMixpanel } from './utils/syncLocalStorageToMixpanel'
import { getUrlParams } from './utils/urls/getUrlParams'
import { stripUrlParam } from './utils/urls/stripUrlParam'

const consoleLoggerInstance = new ConsoleLogger()
setGlobalLogger(consoleLoggerInstance)

/* eslint-enable import/first */
/**
 *
 * @param {import('./modules/types').Store} store
 * @param {boolean} shouldSetLocalUser
 */
const initBundleHelper = (store, shouldSetLocalUser) => {
  const { dispatch, getState } = store
  const state = getState()
  const loggedIn = checkLoggedIn(state)
  const isBot = selectIsBot(state)
  const isMobile = selectIsMobile(state)

  // ignore mixpanel events coming from Cypress
  if (isCypress()) {
    register({ $ignore: true })
  }

  // init bundle
  if (loggedIn) {
    const user = getUser(state)
    register(user)
    identify(user.email)
    Marketing.trackIdentify(user)
  } else if (shouldSetLocalUser) {
    // user is not logged in. We need to set localUser if we successfully decrypted either
    // userToken or fbUserToken.
    const localUserData = localStorageSingleton.get('user')
    if (localUserData) {
      register(localUserData)
      identify(localUserData.email)
    }
    dispatch(setLocalUser(new User(localUserData)))
  }
  if (isNativeAuth(window.location)) {
    function noop() {}
    Object.defineProperty(window, 'localStorage', {
      configurable: true,
      enumerable: true,
      value: { clear: noop, getItem: noop, key: noop, length: 0, removeItem: noop, setItem: noop },
    })
  } else if (!isBot) {
    Api.bundle.init({
      loginHandler: () => {
        Api.bundle
          .begin()
          .then((bundle) => {
            // need this to support cross-tab log-in/out feature
            dispatch(setBundle(bundle))
            if (bundle && bundle.user) {
              if (
                Math.floor(Date.now() / 1000) - bundle.user.lastLoginTimestamp < 60 &&
                !document.cookie
                  .split(';')
                  .some((cookie) => cookie.trim().startsWith(`trackedauth=${bundle.user.id}`))
              ) {
                document.cookie = `trackedauth=${bundle.user.id};path=/;expires=${new Date(Date.now() + 70000).toGMTString()}`
                dispatch(loginHandler(bundle))
                if (bundle.user.lastLoginTimestamp - bundle.user.dateJoinedTimestamp < 60) {
                  // if just signed up, alias user id to email in mixpanel
                  identify(bundle.user.email)
                  alias(bundle.user.id)
                  dispatch(Optimizely.track({ eventKey: ACCOUNT_CREATED }))
                  trackGAEvent(AnalyticsC.LOGIN, 'Create user success', origin)
                  let forYouPayload = {}
                  if (window.location.pathname.startsWith('/for-you')) {
                    forYouPayload = composeForYouPayload()
                    localStorageSingleton.set('forYouEmail', bundle.user.email)
                  }
                  dispatch(
                    trackSignup({
                      origin: `zumper ${isMobile ? 'mobile' : 'desktop'} web`,
                      user: bundle.user,
                    })
                  )
                  if (window.location.pathname) {
                    const pageName = getPageNameFromRouteProps({
                      routeProps: {
                        location: { pathname: window.location.pathname },
                      },
                      isMobile,
                    })
                    track({
                      event: EVENT_NAMES.CREATE_ACCOUNT,
                      payload: { [PROP_NAMES.PAGE_NAME]: pageName },
                    })
                  }
                  Marketing.trackIdentify({ ...bundle.user, ...forYouPayload })
                  Marketing.trackAccountCreation(bundle.user.email)
                } else {
                  // track sign in
                  trackGAEvent(AnalyticsC.LOGIN, 'Sign in success', origin)
                  track({ event: EVENT_NAMES.SIGN_IN })
                }
              } else {
                Marketing.trackIdentify(bundle.user)
              }
            } else if (shouldSetLocalUser) {
              // user is not logged in. We need to set localUser if we successfully decrypted either
              // userToken or fbUserToken.
              const localUserData = localStorageSingleton.get('user')
              dispatch(setLocalUser(new User(localUserData)))
            }
          })
          .catch((err) => {
            if (isFailedToFetchError(err)) {
              logger.warn('failed to fetch when initializing the bundle')
            }
            if (!isRateLimitError(err)) {
              logger.error('error initializing the bundle', err)
            }
          })
          .then(() => dispatch(processPendingMessage()))
      },
      logoutHandler: () => {
        // clearUserInfo nukes sensitive user information from localStorage
        dispatch(
          addSuccessButterBar({
            message: 'You have been successfully logged out!',
          })
        )
        localStorageSingleton.clearUserInfo()
        dispatch(clearBundle())
        if (document.location.pathname.startsWith('/messages')) {
          // if the user logs out on /messages, bring them to the homepage
          dispatch(replace('/'))
        }
      },
    })
  }
}

const setUser = ({ email, name }, fbUserToken, fbListingToken, userToken) => {
  const lastLoginInfo = localStorageSingleton.get('lastLogin')
  const removeAuthToken = email && lastLoginInfo && lastLoginInfo.userEmail !== email
  const { firstName, lastName } = NameUtils.parseFullName(name)
  const localUserData = {
    email,
    first_name: firstName,
    last_name: lastName,
  }
  // different user, remove lastLogin information
  if (removeAuthToken) {
    localStorageSingleton.clearUserInfo()
    localStorageSingleton.del('lastLogin')
    // incorrect user auth token, clear it out
    const form = document.createElement('form')
    const url = new URL(window.location.href)
    if (fbUserToken) {
      url.searchParams.set('fbUserToken', fbUserToken)
    }
    if (fbListingToken) {
      url.searchParams.set('fbListingToken', fbListingToken)
    }
    if (userToken) {
      url.searchParams.set('userToken', userToken)
    }
    form.setAttribute('action', `/zumper-account/ssout?next=${encodeURIComponent(url.toString())}`)
    form.setAttribute('method', 'POST')
    document.body.append(form)
    form.submit()
    // return a promise that never resolves. The line above should unload this page
    return new Promise((resolve) => {})
  }
  saveUserToLocalStorage(localUserData)
  return Promise.resolve({ email, name })
}

/**
 * Create Redux store with initial state (& local storage)
 *
 * @param {import('history').History} history
 * @param {boolean} shouldSetLocalUser
 * @param {string} search
 */
const initHelper = (history, shouldSetLocalUser, search) => {
  const preloadedState = window.__PRELOADED_STATE__ || {}

  const optimizelyConfig = window.__OPTIMIZELY__
  const optimizely = initializeOptimizely(optimizelyConfig, history.location)
  const optimizelyUser = optimizelyConfig.user

  // Optimizely visited experiments
  const visitedExperiments = getVisitedExperiments(optimizely)

  register({ Optimizely: getOptimizelyVariationList(visitedExperiments) })

  optimizely.notificationCenter.addNotificationListener(
    optimizelyEnums.NOTIFICATION_TYPES.ACTIVATE,
    () => {
      const visitedExperiments = getVisitedExperiments(optimizely)
      register({ Optimizely: getOptimizelyVariationList(visitedExperiments) })
    }
  )

  setOptimizelyClient(optimizely)
  const { getGeoLocation } = initializeGeoLocation(window.__GEO_LOCATION__)
  const store = createNzrStore(preloadedState, {
    getGeoLocation,
    history,
    optimizely,
  })
  initSentry(history, store.getState())
  const { dispatch } = store
  const params = new URLSearchParams(search)
  const onboardParam = params?.get('onboard')
  const shouldOpenFilter = onboardParam === 'true'
  // store to redux if it is from paid traffic to open filter on map/list
  shouldOpenFilter && dispatch(setFromPaidMarketing(true))
  // logger.attach(store, desensitizeState)
  initBundleHelper(store, shouldSetLocalUser)

  // Track Pageview now
  Marketing.trackPageload()

  dispatch(event({ name: toTraktorEventName('COLD_LOAD') }))

  // create a map of html for static-hydrating-sections
  const staticMap = getStaticMap()
  const staticRender = createBrowserStaticRender(staticMap)
  dispatch(
    trackWebVitalsMetricsEvent(() =>
      getPageTypeIPromiseImOnlyCallingThisClientSide(store.getState())
    )
  )
  const urlPreviewColorMode = params?.get('preview')
  let colorMode
  if (urlPreviewColorMode === 'light' || urlPreviewColorMode === 'dark') {
    colorMode = urlPreviewColorMode
  } else {
    colorMode = cookies.get('colorMode') ?? routesConstants.INITIAL_COLOR_MODE
  }

  loadableReady(() => {
    performanceBegin('RENDER_REACT_APP')
    performanceBegin('HYDRATE_REACT_APP')
    const app = (
      <StrictMode>
        <StaticHTMLProvider staticRender={staticRender}>
          {/* https://docs.developers.optimizely.com/feature-experimentation/docs/initialize-sdk-react#considerations-when-using-development-mode */}
          <OptimizelyProvider optimizely={optimizely} user={optimizelyUser}>
            <Provider store={store}>
              <ChakraProvider
                resetCSS={false}
                theme={extendTheme({
                  ...ChakraZ4,
                  config: { initialColorMode: colorMode, useSystemColorMode: false },
                })}
              >
                <ReferrerProvider value={document.referrer}>
                  <GeoLocationProvider value={getGeoLocation}>
                    <Router history={history}>
                      <CompatRouter>
                        <AppContainer />
                      </CompatRouter>
                    </Router>
                  </GeoLocationProvider>
                </ReferrerProvider>
              </ChakraProvider>
            </Provider>
          </OptimizelyProvider>
        </StaticHTMLProvider>
      </StrictMode>
    )
    if (process.env.REACT_APP_RENDER === 'client') {
      const root = createRoot(window.document.getElementById('root'))
      root.render(app)
      performanceEnd('RENDER_REACT_APP', { measureDuration: true })
    } else {
      hydrateRoot(window.document.getElementById('root'), app)
      window.__removeEmbeddedStyles && window.__removeEmbeddedStyles()
      // NOTE: development builds do not use mini-css-extract-plugin and embed the css directly into
      // the js chunks using style-loader to improve build performance.
      //
      // We embedded all of the styles in the head (in server/server.js) to mimick the critical
      // styles behavior of a production build. Sadly, style-loader doesn't support picking up
      // pre-embedded styles.
      //
      // Here we remove the embedded styles in order to avoid css conflicts.
      if (IS_NOT_PRODUCTION) {
        // TODO: we need to do this for d-grade bundles to remove duplicate styles
        Array.from(document.querySelectorAll('style.development-build-styles')).forEach((node) =>
          node.parentNode.removeChild(node)
        )
      }
      performanceEnd('HYDRATE_REACT_APP', { measureDuration: true })
    }
    logger.debug('END onload')
  })
}
// Chrome autocomplete bug to be fixed in Chrome 59:
// https://stackoverflow.com/questions/26483541/referenceerror-cant-find-variable-gcrweb
if (!window.__gCrWeb) {
  window.__gCrWeb = {}
}

let hasInitialized = false
const init = () => {
  if (hasInitialized) {
    return
  }
  hasInitialized = true
  if (typeof navigation === 'object') {
    try {
      const url = new URL(navigation.currentEntry.url)
      // Chrome has a bug like this:
      // 1. Go through Zumper's SSO or SSOut flow
      // 2. Flow ends with a redirect to https://username:password@....zumper.com/...
      // 3. User tries to perform a warm navigation
      // 4. Chrome throws an error:
      //    SecurityError: Failed to execute 'pushState' on 'History': A history state object with URL
      //    'https://xyz.zumper.com/zumper-account/signin?next=%2F' cannot be created in a document with
      //    origin 'https://xyz.zumper.com' and URL 'https://username:password@xyz.zumper.com/'.
      //
      // When this bug is triggered, window.location.href doesn't include the username and password, but
      // navigation.currentEntry.url does. To workaround the bug, we detect and remove them. Once the page
      // has been reloaded without them, subsequent warm navigations will work
      if (url.username || url.password) {
        url.username = url.password = ''
        window.location.replace(url)
      }
    } catch {}
  }
  // Grab the state from a global injected into server-generated HTML
  const history = getBrowserHistory()
  const { location } = history
  const { search } = location
  const { createAlert, fbListingToken, fbUserToken, opt, optFeature, userToken } = getUrlParams()

  // remove forced optimizely experiment and variation
  if (opt) {
    history.replace(stripUrlParam(location, 'opt'))
  }
  if (optFeature) {
    history.replace(stripUrlParam(location, 'optFeature'))
  }

  logger.info({ history })
  logger.debug('START onload')
  if (window.__polyfilled) {
    logger.debug('polyfilled')
  }
  // initialize local-storage
  localStorageSingleton.init()
  // Add C,R,T and POV Tokens from URL to localstorage
  localStorageSingleton.addTrackingToLocalStorage(history)
  // Add Recombee recommId from URL to localstorage
  localStorageSingleton.addRecombeeToLocalStorage(getRecommIdQueryParam())

  // Temporarily persist all tokens for For You initialization
  addForYouTokens(search)

  // Sync validated localStorage UTM params to Mixpanel
  syncLocalStorageToMixpanel()

  const promises = []

  let decryptedUser = false
  // Note: fbUserToken isn't related to Facebook login (we no longer support Facebook login). It's just
  // a URL param that we add in some emails we send, that allows us to see who we sent the email to. If
  // the email address contained in the token doesn't match the already-logged-in user, we log the user
  // out
  if (fbUserToken || fbListingToken) {
    const { location } = history
    let { search } = location
    search = search
      .replace(/([?&])(?:fbUserToken=[^&]+)[&]/, '$1')
      .replace(/(?:[?&])(?:fbUserToken=[^&]+)$/, '')
      .replace(/([?&])(?:fbListingToken=[^&]+)[&]/, '$1')
      .replace(/(?:[?&])(?:fbListingToken=[^&]+)$/, '')
    history.replace({ ...location, search })
    if (fbUserToken) {
      // use separate fbUserToken to avoid race condition when userToken is removed from the url
      promises.push(
        Api.decryptToken
          .query({ token: fbUserToken })
          .then((user) => {
            decryptedUser = true
            setUser(user, fbUserToken, fbListingToken, userToken)
          })
          .then(({ email, name }) => {
            // If the user comes from a notify me link in marketplace_email.html, we go ahead and
            // track the user. We identify this type of user by checking the 'createAlert' param.
            if (createAlert) {
              const { firstName, lastName } = NameUtils.parseFullName(name)
              Marketing.trackIdentify({ firstName, lastName, email, isFacebook: true })
              if (fbListingToken) {
                return Api.decryptToken
                  .query({ token: fbListingToken })
                  .then(({ listing_id: listingId }) => {
                    if (listingId) {
                      Marketing.trackViewDetail(null, listingId, email)
                      Marketing.trackSendLeads({ listingIds: [listingId], email, isFacebook: true })
                    } else {
                      logger.debug('missing listing id to track send lead')
                    }
                  })
                  .catch((error) => {
                    logger.debug(
                      'client.js, error using decryptToken on fbListingToken',
                      fbListingToken,
                      error
                    )
                  })
              }
            }
          })
          .catch((error) => {
            logger.debug('error occurred with decrypt fb user token:', fbUserToken, error)
          })
      )
    }
  }
  if (userToken) {
    // Remove user token
    const { location } = history
    let { search } = location
    search = search
      .replace(/([?&])(?:userToken=[^&]+)[&]/, '$1')
      .replace(/(?:[?&])(?:userToken=[^&]+)$/, '')
    history.replace({ ...location, search })

    promises.push(
      Api.decryptToken
        .query({ token: userToken })
        .then((user) => {
          decryptedUser = true
          setUser(user, fbUserToken, fbListingToken, userToken)
        })
        .catch((error) => {
          logger.debug('client.js, error using decryptToken on userToken', userToken, error)
        })
    )
  }
  // Create Redux store with initial state (& local storage)
  Promise.all(promises).then(() => {
    initHelper(history, decryptedUser, search)
  })
}

const copyAttributes = (source, dest) => {
  const attrs = source.attributes
  let attr
  for (let i = attrs.length - 1; i >= 0; i--) {
    attr = attrs[i]
    if (attr.specified && !dest.hasAttribute(attr.name)) {
      dest.setAttribute(attr.name, attr.value)
    }
  }
  dest.onerror = source.onerror
  dest.onload = source.onload
}

window.document.documentElement.addEventListener(
  'error',
  (e) => {
    const target = e.target
    const nodeName = target.nodeName.toLowerCase()

    if (nodeName === 'link' || nodeName === 'script') {
      const srcAttr = nodeName === 'script' ? 'src' : 'href'
      const href = target.getAttribute(srcAttr)

      if (href && typeof URL === 'function') {
        const url = new URL(href, window.document.location.href)
        const thisHour = String(Math.floor(Date.now() / 3600000))

        if (url.searchParams.get('cache') !== thisHour && target.parentNode) {
          url.searchParams.set('cache', thisHour)
          const clone = window.document.createElement(nodeName)
          copyAttributes(target, clone)
          clone.setAttribute(srcAttr, url.toString())
          target.parentNode.replaceChild(clone, target)
          e.stopImmediatePropagation()
        }
      }
    }
  },
  true
)

if (!hasInitialized) {
  window.requestAnimationFrame(init)
}
